import { z } from 'zod';
import { isoDate, paginacao, taxa } from './primitives.js';

/** Digitos de um codigo fiscal digitado com ou sem pontuacao ("8471.30.12", "21.001.00"). */
const soDigitos = (v: string): string => v.replace(/[.\-\s]/g, '');

/** Codigo fiscal de `n` digitos (aceita pontos/tracos/espacos e normaliza para so digitos). */
const codigoFiscal = (n: number, nome: string) =>
  z
    .string()
    .trim()
    .transform(soDigitos)
    .pipe(z.string().regex(new RegExp(`^\\d{${n}}$`), `${nome} deve ter ${n} dígitos`));

/** CST de ICMS (regime normal) e CSOSN (Simples Nacional). Lista do XARP (stockable-form: cstICMS) + CSOSN. */
export const CST_ICMS = ['00', '10', '20', '30', '40', '41', '50', '51', '60', '70', '90'] as const;
export const CSOSN_ICMS = ['101', '102', '103', '201', '202', '203', '300', '400', '500', '900'] as const;
/** CST de PIS/COFINS (tabela oficial: saida 01-09, outras 49, entrada 50-56 e 60-67, 70-75, 98, 99). */
export const CST_PIS_COFINS = [
  '01', '02', '03', '04', '05', '06', '07', '08', '09', '49',
  '50', '51', '52', '53', '54', '55', '56', '60', '61', '62', '63', '64', '65', '66', '67', '70', '71', '72', '73', '74', '75', '98', '99',
] as const;
/** CST de IPI (entrada 00-05 e 49; saida 50-55 e 99). */
export const CST_IPI = ['00', '01', '02', '03', '04', '05', '49', '50', '51', '52', '53', '54', '55', '99'] as const;

const cstEm = (lista: readonly string[], nome: string) =>
  z
    .string()
    .trim()
    .refine((v) => lista.includes(v), `${nome} inválido`);

const cstIcmsOuCsosn = cstEm([...CST_ICMS, ...CSOSN_ICMS], 'CST/CSOSN de ICMS');

/**
 * Cadastro fiscal do item (Sessao 6, item 7): SO CADASTRO. Nenhum destes campos entra em calculo (o motor recebe so
 * PIS/COFINS/ICMS/ISS/IPI). NCM 8 digitos, CEST 7, NBS 9 (so SERVICO), CST IBS/CBS 3, cClassTrib 6; IBS/CBS/IS = reforma
 * tributaria, congelada (SPEC 12).
 */
const camposFiscais = {
  ncm: codigoFiscal(8, 'NCM').optional(),
  cest: codigoFiscal(7, 'CEST').optional(),
  cstPis: cstEm(CST_PIS_COFINS, 'CST do PIS').optional(),
  cstCofins: cstEm(CST_PIS_COFINS, 'CST do COFINS').optional(),
  cstIpi: cstEm(CST_IPI, 'CST do IPI').optional(),
  /** ICMS ST (sim/nao) do cadastro do XARP (`taxes.ST`). */
  icmsSt: z.boolean().optional(),
  nbs: codigoFiscal(9, 'NBS').optional(),
  cstIbsCbs: codigoFiscal(3, 'CST do IBS/CBS').optional(),
  cClassTrib: codigoFiscal(6, 'cClassTrib').optional(),
  reducaoIbs: taxa.optional(),
  reducaoCbs: taxa.optional(),
  impostoSeletivo: z.boolean().optional(),
  aliquotaIs: taxa.optional(),
};

/** ProductTaxes com vigencia (SPEC 9.5.1, 2.5). Taxas em fracao; 0 = ausente. Valor nunca e editado. */
export const novoItemTributoSchema = z
  .object({
    validFrom: isoDate,
    pis: taxa.default('0'),
    cofins: taxa.default('0'),
    icms: taxa.default('0'),
    iss: taxa.default('0'),
    ipi: taxa.default('0'),
    cstIcms: cstIcmsOuCsosn.optional(),
    icmsRbc: taxa.optional(),
    motivo: z.string().trim().min(1).max(300).optional(),
    ...camposFiscais,
  })
  .superRefine((v, ctx) => {
    if (v.icmsRbc !== undefined && v.cstIcms !== undefined && v.cstIcms !== '20' && v.cstIcms !== '70') {
      ctx.addIssue({ code: 'custom', path: ['icmsRbc'], message: 'A redução da base de cálculo do ICMS só vale com CST 20 ou 70' });
    }
    if (v.impostoSeletivo === true && v.aliquotaIs === undefined) {
      ctx.addIssue({ code: 'custom', path: ['aliquotaIs'], message: 'Informe a alíquota do Imposto Seletivo' });
    }
    if (v.impostoSeletivo !== true && v.aliquotaIs !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['aliquotaIs'], message: 'A alíquota do Imposto Seletivo só vale quando o item está sujeito a ele' });
    }
    for (const campo of ['reducaoIbs', 'reducaoCbs', 'aliquotaIs'] as const) {
      const x = v[campo];
      if (x !== undefined && Number(x) > 1) ctx.addIssue({ code: 'custom', path: [campo], message: 'Percentual acima de 100%' });
    }
  });
export type NovoItemTributoDto = z.infer<typeof novoItemTributoSchema>;

export const listarItemTributosSchema = paginacao;
export type ListarItemTributosQuery = z.infer<typeof listarItemTributosSchema>;

export interface ItemTributoResponse {
  id: string;
  itemId: string;
  validFrom: string;
  validTo: string | null;
  pis: string;
  cofins: string;
  icms: string;
  iss: string;
  ipi: string;
  cstIcms: string | null;
  icmsRbc: string | null;
  motivo: string | null;
  createdAt: string;
  /** Cadastro fiscal (so cadastro, fora do calculo). Sempre presentes na resposta da API (null/false = nao informado). */
  ncm?: string | null;
  cest?: string | null;
  icmsSt?: boolean;
  cstPis?: string | null;
  cstCofins?: string | null;
  cstIpi?: string | null;
  nbs?: string | null;
  cstIbsCbs?: string | null;
  cClassTrib?: string | null;
  reducaoIbs?: string | null;
  reducaoCbs?: string | null;
  impostoSeletivo?: boolean;
  aliquotaIs?: string | null;
}

/**
 * Percentual digitado ("18", "18,5", "0,65") -> taxa em fracao como string ("0.18", "0.185", "0.0065").
 * So desloca o ponto decimal na string (sem aritmetica, sem number). Invalido ou acima de 100%: null.
 */
export function percentualParaTaxa(texto: string): string | null {
  const m = /^(\d{1,3})(?:[.,](\d{1,6}))?$/.exec(texto.trim());
  if (!m) return null;
  const inteiro = (m[1] ?? '0').replace(/^0+(?=\d)/, '');
  const frac = m[2] ?? '';
  const digitos = `${inteiro}${frac}`;
  const ponto = inteiro.length - 2;
  const bruto = ponto <= 0 ? `0.${'0'.repeat(-ponto)}${digitos}` : `${digitos.slice(0, ponto)}.${digitos.slice(ponto)}`;
  let [i = '0', f = ''] = bruto.split('.');
  i = i.replace(/^0+(?=\d)/, '');
  f = f.replace(/0+$/, '');
  if (i.length > 1 || (i === '1' && f !== '')) return null; // acima de 100%
  return f ? `${i}.${f}` : i;
}

const percentual = z
  .string()
  .trim()
  .refine((v) => v === '' || percentualParaTaxa(v) !== null, 'Percentual invalido (0 a 100, ate 6 casas)');

/**
 * Formulario "nova vigencia" do item: aliquotas em PERCENTUAL como o usuario digita; vazio = 0 (ausente).
 * A saida ja e o corpo de `novoItemTributoSchema` (taxas em fracao), que valida de novo: fonte unica das regras.
 */
export const novoItemTributoFormSchema = z
  .object({
    validFrom: isoDate,
    pis: percentual,
    cofins: percentual,
    icms: percentual,
    iss: percentual,
    ipi: percentual,
    cstIcms: z.string().trim().max(4, 'Ate 4 caracteres'),
    motivo: z.string().trim().max(300, 'Ate 300 caracteres'),
    // Cadastro fiscal (opcional; vazio = nao informado). Percentuais como digitados; sao convertidos em fracao.
    ncm: z.string().trim().optional(),
    cest: z.string().trim().optional(),
    cstPis: z.string().trim().optional(),
    cstCofins: z.string().trim().optional(),
    cstIpi: z.string().trim().optional(),
    icmsSt: z.boolean().optional(),
    nbs: z.string().trim().optional(),
    cstIbsCbs: z.string().trim().optional(),
    cClassTrib: z.string().trim().optional(),
    reducaoIbs: percentual.optional(),
    reducaoCbs: percentual.optional(),
    impostoSeletivo: z.boolean().optional(),
    aliquotaIs: percentual.optional(),
  })
  .transform((v): z.input<typeof novoItemTributoSchema> => {
    const taxa = (t: string) => (t === '' ? '0' : (percentualParaTaxa(t) ?? t));
    return {
      validFrom: v.validFrom,
      pis: taxa(v.pis),
      cofins: taxa(v.cofins),
      icms: taxa(v.icms),
      iss: taxa(v.iss),
      ipi: taxa(v.ipi),
      ...(v.cstIcms ? { cstIcms: v.cstIcms } : {}),
      ...(v.motivo ? { motivo: v.motivo } : {}),
      ...(v.ncm ? { ncm: v.ncm } : {}),
      ...(v.cest ? { cest: v.cest } : {}),
      ...(v.cstPis ? { cstPis: v.cstPis } : {}),
      ...(v.cstCofins ? { cstCofins: v.cstCofins } : {}),
      ...(v.cstIpi ? { cstIpi: v.cstIpi } : {}),
      ...(v.icmsSt !== undefined ? { icmsSt: v.icmsSt } : {}),
      ...(v.nbs ? { nbs: v.nbs } : {}),
      ...(v.cstIbsCbs ? { cstIbsCbs: v.cstIbsCbs } : {}),
      ...(v.cClassTrib ? { cClassTrib: v.cClassTrib } : {}),
      ...(v.reducaoIbs ? { reducaoIbs: taxa(v.reducaoIbs) } : {}),
      ...(v.reducaoCbs ? { reducaoCbs: taxa(v.reducaoCbs) } : {}),
      ...(v.impostoSeletivo !== undefined ? { impostoSeletivo: v.impostoSeletivo } : {}),
      ...(v.aliquotaIs ? { aliquotaIs: taxa(v.aliquotaIs) } : {}),
    };
  })
  .pipe(novoItemTributoSchema);
export type NovoItemTributoFormEntrada = z.input<typeof novoItemTributoFormSchema>;
